/**
 * "Highlight for Alert" predicate — decides whether a rendered control gets the red ALERT box.
 *
 * Used by src/pages/Forms/views/renderer/ControlView.vue, which renders BOTH the staff
 * ViewSubmission screen and the Puppeteer-captured PDF that is filed into Open Dental imaging.
 * Both set viewSubmissionMode=true; the patient-fill view (ViewForm) leaves it false and is never
 * highlighted. No backend code reads the alert flags — this file is the whole feature.
 *
 * THE "None" RULE (Open Dental Data Fields)
 * ----------------------------------------
 * An `opendentaldata` control (ALLERGIES / DISEASES / MEDICATIONS) submits an ARRAY OF OBJECTS
 * `[{text, value}]`, and the renderer synthesises a "None" option meaning "I have no
 * allergies/diseases/medications" (OpenDentalDataControl.vue — `{ text: 'None', value: 'none' }`,
 * or the practice's real OD definition, which keeps a numeric DefNum but is still titled "None").
 * A patient who answers "None" has given the SAFEST possible answer, so highlighting it in red is
 * backwards — that is exactly what Harrisburg Smiles hit on 2026-09-08.
 *
 * `isNoneOption` below mirrors forms-service/forms-api.js (~2957-2962) EXACTLY — the backend uses
 * the same test to strip "None" before writing to Open Dental. Keep the two in sync: if one starts
 * treating an item as "None" and the other does not, the PDF and the chart disagree.
 *
 * Import-free on purpose: cypress/node-tests/forms-alert-highlight.test.js loads and executes THIS
 * file (strip the trailing `export {...}` + `new Function`), so the tests verify production code
 * rather than a copy. Do not add an `import` here.
 */

const CHOICE_TYPES = ['radio', 'checkbox', 'dropDown'];
const OD_DATA_TYPE = 'opendentaldata';

/**
 * "Is this submitted item the None sentinel?"
 * MIRRORS forms-service/forms-api.js isNoneOption — byte-for-byte behaviour. Do not 'improve' it
 * on one side only. Note it is an EXACT match, so a real allergy called "None of the above" is a
 * genuine selection and still alerts.
 *
 * @param {{text?: string, value?: *}} item
 * @returns {boolean}
 */
function isNoneOption(item) {
    if (!item) return false;
    const textLower = (item.text || '').toLowerCase();
    const valueLower = String(item.value || '').toLowerCase();
    return textLower === 'none' || valueLower === 'none';
}

/**
 * "Did the patient give a real answer to this question?"
 * Type-aware: handles strings, arrays, booleans (switch), and objects (signature/file).
 * Note number 0 counts as an answer; an unticked switch (false) does not.
 *
 * @param {*} value
 * @returns {boolean}
 */
function hasNonEmptyAnswer(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
}

/**
 * For radio/dropdown (single value) → true if patient's value is in flaggedValues.
 * For checkbox (array of values) → true if patient's array intersects flaggedValues (ANY-match).
 *
 * @param {Array} flaggedValues
 * @param {*} patientValue
 * @returns {boolean}
 */
function answerMatchesAny(flaggedValues, patientValue) {
    if (patientValue === null || patientValue === undefined) return false;
    if (Array.isArray(patientValue)) {
        return patientValue.some(v => flaggedValues.includes(v));
    }
    return flaggedValues.includes(patientValue);
}

/**
 * Alert rule for an `opendentaldata` control.
 *
 *  1. Drop every submitted item that is the None option → the "real selections".
 *  2. No real selections (empty, undefined, or a lone None) → NO highlight.
 *  3. control.alertOptions absent/empty → highlight on ANY real selection. This is the default,
 *     so an office that already ticked "Highlight for Alert" needs no reconfiguration.
 *  4. control.alertOptions non-empty → highlight only when a real selection's String(value)
 *     matches one of them (ANY-match, same rule as checkbox). Values are site-specific DefNums,
 *     so both sides are String()-coerced; None is never a match even if someone lists it.
 *
 * @param {Object} control
 * @param {*} patientValue - expected `[{text, value}]`
 * @returns {boolean}
 */
function odDataAlertTriggered(control, patientValue) {
    const real = Array.isArray(patientValue)
        ? patientValue.filter(it => it && typeof it === 'object' && !isNoneOption(it))
        : [];
    if (real.length === 0) return false;

    const alertValues = (Array.isArray(control.alertOptions) ? control.alertOptions : [])
        .filter(o => o && typeof o === 'object' && o.value !== undefined && o.value !== null && !isNoneOption(o))
        .map(o => String(o.value));

    if (alertValues.length === 0) return true;

    return real.some(it => alertValues.includes(String(it.value)));
}

/**
 * The full predicate.
 *
 * Gated on viewSubmissionMode so the highlight only reaches the reviewing audience (office staff
 * on ViewSubmission + the Puppeteer-captured PDF), NOT the patient filling the form.
 *
 * Master switch is control.highlightForAlert; when on:
 *  - Open Dental Data Fields → odDataAlertTriggered (the None rule above).
 *  - Choice controls (radio/checkbox/dropDown) with per-option isAlert flags: trigger when the
 *    answer matches at least one flagged option (ANY-match).
 *  - Choice controls with NO per-option flags (legacy/simple use): fall back to "any non-empty
 *    answer triggers" so existing forms keep working.
 *  - Everything else (input/date/signature/…): trigger on any non-empty answer.
 *
 * @param {{control?: Object, patientValue?: *, viewSubmissionMode?: boolean}} args
 * @returns {boolean}
 */
function shouldHighlightAlert(args = {}) {
    const control = args.control;
    const patientValue = args.patientValue;
    const viewSubmissionMode = args.viewSubmissionMode;

    if (!viewSubmissionMode) return false;
    if (!control || control.highlightForAlert !== true) return false;

    if (control.type === OD_DATA_TYPE) {
        return odDataAlertTriggered(control, patientValue);
    }

    if (CHOICE_TYPES.includes(control.type)) {
        const items = Array.isArray(control.items) ? control.items : [];
        const flaggedValues = items.filter(i => i && i.isAlert).map(i => i.value);
        if (flaggedValues.length > 0) {
            return answerMatchesAny(flaggedValues, patientValue);
        }
        // No per-option flags → fall back to "any non-empty answer triggers"
        return hasNonEmptyAnswer(patientValue);
    }

    return hasNonEmptyAnswer(patientValue);
}

export { isNoneOption, hasNonEmptyAnswer, answerMatchesAny, odDataAlertTriggered, shouldHighlightAlert };
